import { memo, useCallback, useMemo } from 'react';
import { SOFQuestionCheckboxProps } from './SOFQuestionCheckbox.types';
import { InputChangeHandler } from '../../SOFQuestions/SOFQuestion.types';
import { Box, Checkbox, Text, TextProps } from '@bonanza-social/moonlight';
import SOFQuestionInput from '../../SOFQuestionInput/SOFQuestionInput';
import { useTranslation } from '@bonanza-social/i18n';
import { InputSubType, InputType } from '../../SOF.types';
import { TextInputProps } from 'react-native';

function SOFQuestionCheckbox({
  text,
  questionId,
  controlId,
  answers,
  onValue,
  onAdditionalTextChange,
  inputType,
  error,
  inputRef,
  onSubmitEditing,
}: SOFQuestionCheckboxProps) {
  const answer = answers[controlId];
  const value = !!answers[controlId].answer;
  const { t } = useTranslation('sof');

  const handleCheckboxValue = useCallback(
    (checked: boolean) =>
      onValue(questionId, controlId, checked, text, inputType),
    [questionId, controlId, text, inputType, onValue],
  );

  const handleInputChange = useCallback<InputChangeHandler>(
    (...args) => onAdditionalTextChange(...args),
    [onAdditionalTextChange],
  );

  const { amountError, jobTitleError, companyNameError } = useMemo(
    () => ({
      amountError: error?.amount?.find((el) => el.controlId === controlId),
      jobTitleError: error?.jobTitle?.find((el) => el.controlId === controlId),
      companyNameError: error?.companyName?.find(
        (el) => el.controlId === controlId,
      ),
    }),
    [error?.amount, error?.jobTitle, error?.companyName, controlId],
  );

  const disableCheckbox = useMemo(() => {
    if (
      controlId === '2-3' &&
      (answers['2-1'].answer === 'question2.option.1' ||
        answers['2-1'].answer === 'Employed' ||
        answers['2-2'].answer === 'question2.option.2' ||
        answers['2-2'].answer === 'Self-Employed')
    ) {
      return true;
    }

    if (
      (controlId === '2-1' || controlId === '2-2') &&
      (answers['2-3'].answer === 'question2.option.3' ||
        answers['2-3'].answer === 'Unemployed')
    ) {
      return true;
    }

    return false;
  }, [answers, controlId]);

  const renderInput = (
    label: TextProps['locKey'],
    errorType?: string,
    inputValue?: string,
    subType?: InputSubType,
    typeOverride?: InputType,
    keyboardType?: TextInputProps['keyboardType'],
  ) => (
    <SOFQuestionInput
      value={inputValue}
      labelNS="account"
      labelKey={label}
      questionId={questionId}
      error={errorType}
      inputType={typeOverride ?? inputType}
      inputSubType={subType}
      inputRef={inputRef}
      controlId={controlId}
      onChange={handleInputChange}
      onSubmitEditing={onSubmitEditing}
      keyboardType={keyboardType}
    />
  );

  return (
    <Box gap="space-200">
      <Checkbox
        checked={value}
        onChange={handleCheckboxValue}
        disabled={disableCheckbox}
      >
        <Text variant="input-text" color="darkSurfacePrimary">
          {t(text)}
        </Text>
      </Checkbox>
      {value &&
        (inputType === InputType.EMPLOYED ||
          inputType === InputType.SELF_EMPLOYED) && (
          <>
            {renderInput(
              'jobTitle',
              jobTitleError?.text,
              answer.jobTitle,
              InputSubType.JOB_TITLE,
            )}
            {renderInput(
              'companyName',
              companyNameError?.text,
              answer.companyName,
              InputSubType.COMPANY_NAME,
            )}
          </>
        )}

      {value &&
        inputType === InputType.EMPLOYED &&
        renderInput(
          'industry',
          error?.industry,
          answer.industry,
          InputSubType.INDUSTRY,
        )}

      {value &&
        inputType === InputType.SELF_EMPLOYED &&
        renderInput(
          'natureOfBusiness',
          error?.natureOfBusiness,
          answer.natureOfBusiness,
          InputSubType.NATURE_OF_BUSINESS,
        )}

      {value &&
        inputType === InputType.INCOME &&
        renderInput(
          'incomeType',
          error?.incomeType,
          answer.incomeType,
          undefined,
          InputType.INCOME,
        )}

      {value &&
        (inputType === InputType.INCOME || inputType === InputType.AMOUNT) &&
        renderInput(
          'amount',
          amountError?.text,
          answer.amount,
          undefined,
          InputType.AMOUNT,
          'numeric',
        )}
    </Box>
  );
}

export default memo(SOFQuestionCheckbox);
